import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileCheck,
  LoaderCircle,
  RotateCcw,
  X,
} from "lucide-react";
import { useMikroSuggestedRequestWorkspace } from "./useMikroSuggestedRequestWorkspace";
import { dateKey, todayKey, formatDate, formatDateTime, formatQuantity, type SuggestedRequestDraft } from "./mikro-suggestion-model";
export type { SuggestedRequestDraft } from "./mikro-suggestion-model";

export function MikroSuggestedRequestComposer({
  onCancel,
  onManualEntry,
  onApply,
}: {
  onCancel: () => void;
  onManualEntry: () => void;
  onApply: (draft: SuggestedRequestDraft) => void;
}) {
  const { result, selectedIndex, includeOverdue, setIncludeOverdue, includeMinimumStock, setIncludeMinimumStock, loading, error, setReloadKey, items, suggestion, calculation, move, applySuggestion } = useMikroSuggestedRequestWorkspace({ onApply });

  return (
    <section
      className="inline-form-card mikro-suggestion-composer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mikro-suggestion-title"
    >
      <header className="mikro-suggestion-header">
        <div>
          <span className="section-kicker">Mikro&apos;dan önerilen ihtiyaç</span>
          <h2 id="mikro-suggestion-title">Satın alma talebi oluştur</h2>
        </div>
        <div className="mikro-suggestion-header-actions">
          <span className="mikro-readonly-source">
            <strong>Mikro ERP · Salt okunur</strong>
            <small>{formatDateTime(result?.refreshedAt ?? null)}</small>
          </span>
          <button className="icon-button" type="button" aria-label="Formu kapat" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="mikro-suggestion-state" role="status">
          <LoaderCircle className="is-spinning" size={22} />
          <div><strong>Mikro ihtiyaçları hesaplanıyor</strong><p>Açık iş emirleri, stoklar ve zamanında gelecek siparişler okunuyor.</p></div>
        </div>
      ) : error ? (
        <div className="mikro-suggestion-state is-error" role="alert">
          <AlertTriangle size={22} />
          <div><strong>İhtiyaç önerileri alınamadı</strong><p>{error}</p></div>
          <button className="secondary-button" type="button" onClick={() => setReloadKey((value) => value + 1)}>
            <RotateCcw size={15} /> Yeniden dene
          </button>
        </div>
      ) : !suggestion ? (
        <div className="mikro-suggestion-state" role="status">
          <FileCheck size={22} />
          <div><strong>Satın alma açığı görünmüyor</strong><p>Açık iş emirleri mevcut stok ve zamanında gelecek miktarla karşılanıyor.</p></div>
        </div>
      ) : (
        <div className="mikro-suggestion-body">
          <div className="mikro-suggestion-material">
            <div>
              <span className="material-code">{suggestion.materialCode}</span>
              <h3>{suggestion.materialName || "Adsız malzeme"}</h3>
              <p>Depo {suggestion.warehouseCode}{suggestion.warehouseName ? ` · ${suggestion.warehouseName}` : ""} · Ana birim {suggestion.unit || "—"}</p>
            </div>
            <div className="mikro-suggestion-quantity">
              <small>Önerilen kesin ihtiyaç</small>
              <strong>{formatQuantity(calculation.requestQuantity, suggestion.unit)}</strong>
            </div>
          </div>

          {items.length > 1 ? (
            <div className="mikro-suggestion-pager" aria-label="İhtiyaç önerileri">
              <button type="button" aria-label="Önceki öneri" onClick={() => move(-1)}><ChevronLeft size={17} /></button>
              <span><strong>{selectedIndex + 1}</strong> / {items.length} kritik ihtiyaç</span>
              <button type="button" aria-label="Sonraki öneri" onClick={() => move(1)}><ChevronRight size={17} /></button>
            </div>
          ) : null}

          <div className="mikro-suggestion-metrics" aria-label="İhtiyaç özeti">
            <div><span>Bugünkü stok</span><strong>{formatQuantity(suggestion.onHandQuantity, suggestion.unit)}</strong></div>
            <div><span>{suggestion.workOrderCount} açık iş emri</span><strong>{formatQuantity(suggestion.totalRequiredQuantity, suggestion.unit)}</strong></div>
            <div><span>Zamanında açık giriş</span><strong>{formatQuantity(suggestion.inboundQuantity, suggestion.unit)}</strong></div>
            <div><span>Tedarik süresi</span><strong>{suggestion.leadTimeDays.toLocaleString("tr-TR")} gün</strong></div>
          </div>

          {suggestion.overdueWorkOrderCount > 0 ? (
            <div className="mikro-suggestion-warning" role="alert">
              <AlertTriangle size={18} />
              <div>
                <strong>{formatQuantity(suggestion.overdueRequiredQuantity, suggestion.unit)} tutarındaki {suggestion.overdueWorkOrderCount} iş emrinin plan tarihi geçmiş.</strong>
                <small>Talep miktarına katılmadan önce bu iş emirlerinin hâlâ üretilecek olduğunu planlamacı doğrulamalı.</small>
              </div>
            </div>
          ) : null}

          <div className="mikro-suggestion-options">
            <label><input type="checkbox" checked={includeOverdue} onChange={(event) => setIncludeOverdue(event.target.checked)} /> Gecikmiş açık iş emirlerini dahil et</label>
            {suggestion.minimumStockQuantity > 0 ? (
              <label><input type="checkbox" checked={includeMinimumStock} onChange={(event) => setIncludeMinimumStock(event.target.checked)} /> {formatQuantity(suggestion.minimumStockQuantity, suggestion.unit)} minimum stok hedefini dahil et</label>
            ) : null}
          </div>

          <div className="mikro-suggestion-table-wrap">
            <table className="mikro-suggestion-table">
              <thead><tr><th>Tarih</th><th>Hareket</th><th>Değişim</th><th>Tahmini stok</th></tr></thead>
              <tbody>
                <tr><td>Bugün</td><td>Mevcut kullanılabilir stok</td><td>Başlangıç</td><td>{formatQuantity(suggestion.onHandQuantity, suggestion.unit)}</td></tr>
                {calculation.rows.map((row) => (
                  <tr key={row.id} className={`${row.shortage ? "has-shortage" : ""}${row.ignored ? " is-ignored" : ""}`}>
                    <td>{formatDate(row.eventAt)}</td><td>{row.label}</td><td>{row.ignored ? "Hariç" : `${row.change > 0 ? "+" : "−"}${formatQuantity(Math.abs(row.change), suggestion.unit)}`}</td><td>{formatQuantity(row.balance, suggestion.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mikro-suggestion-results" aria-live="polite">
            <div><span>Kesin satın alma ihtiyacı</span><strong>{formatQuantity(calculation.requestQuantity, suggestion.unit)}</strong></div>
            <div><span>İlk açık tarihi</span><strong>{calculation.firstShortageAt ? formatDate(calculation.firstShortageAt) : "Açık oluşmuyor"}</strong></div>
            <div><span>En geç sipariş tarihi</span><strong>{calculation.latestOrderAt ? `${formatDate(calculation.latestOrderAt)}${(dateKey(calculation.latestOrderAt) ?? "9999") < todayKey() ? " · geçmiş" : ""}` : "Gerekmiyor"}</strong></div>
          </div>
        </div>
      )}

      <footer className="mikro-suggestion-footer">
        <button className="text-button" type="button" onClick={onManualEntry}>Manuel talep gir</button>
        <div>
          <button className="secondary-button" type="button" onClick={onCancel}>Vazgeç</button>
          <button className="approve-button" type="button" disabled={!suggestion || calculation.requestQuantity <= 0} onClick={applySuggestion}>
            <FileCheck size={15} /> Talep formunu önizle
          </button>
        </div>
      </footer>
    </section>
  );
}
